import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useCart } from "../lib/cart";
import { useSiteConfig } from "../lib/siteConfig";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;
  const { add } = useCart();
  const { products_available } = useSiteConfig();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/products/${slug}`).then(r => r.ok ? r.json() : Promise.reject()).then(setP).catch(() => nav("/"));
    setImgIdx(0); setSize(null); setQty(1); setErr(null);
    document.title = "Product · Philadelphia Freeway";
  }, [slug, API, nav]);

  if (!p) return <div className="min-h-screen bg-black text-white"><Navbar/><div className="p-20 text-center font-mono">Loading…</div></div>;

  const needsSize = p.variants.length > 1;
  const onAdd = () => {
    if (needsSize && !size) { setErr("Please select a size."); return; }
    const chosen = needsSize ? size : p.variants[0]?.size || "ONE SIZE";
    add(p.slug, chosen, qty, { name: p.name, image: p.images[0], collection: p.collection, category: p.category });
  };

  return (
    <div className="relative bg-white min-h-screen text-black flex flex-col" style={{ background: "white" }}>
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 pt-24 md:pt-28 pb-16 md:pb-24">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 hover:text-black mb-6"><ArrowLeft className="w-4 h-4"/> Back</Link>
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            <div>
              <div className="relative aspect-square bg-neutral-100 overflow-hidden">
                <img src={p.images[imgIdx]} alt={p.name} className="w-full h-full object-contain p-6"/>
              </div>
              {p.images.length > 1 && (
                <div className="mt-3 flex gap-2">
                  {p.images.map((src, i) => (
                    <button key={i} onClick={() => setImgIdx(i)} data-testid={`product-thumb-${i}`}
                      className={`w-20 h-20 border ${i===imgIdx ? "border-black" : "border-black/15"} bg-neutral-100`}>
                      <img src={src} alt="" className="w-full h-full object-contain p-2"/>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B48A00]">
                {p.collection === "planes-fta" ? "FTA × Paper Planes" : "FTA Fall Collection"}
              </div>
              <h1 data-testid="product-heading" className="mt-2 font-wide uppercase text-3xl md:text-4xl leading-[1.05]">{p.name}</h1>
              <div className="mt-3 font-mono text-lg">${p.price.toFixed(2)}</div>

              {needsSize ? (
                <div className="mt-8">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-3">Select Size</div>
                  <div className="flex flex-wrap gap-2">
                    {p.variants.map((v) => (
                      <button key={v.size} onClick={() => { setSize(v.size); setErr(null); }}
                        data-testid={`size-${v.size}`}
                        className={`min-w-[64px] px-4 py-2 border font-mono text-sm ${size===v.size ? "bg-black text-white border-black" : "bg-white text-black border-black/25 hover:border-black"}`}>
                        {v.size}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mt-8">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-3">Size</div>
                  <div className="inline-block px-4 py-2 border border-black/25 bg-neutral-50 font-mono text-sm" data-testid="size-ONE SIZE">
                    {p.variants[0]?.size || "ONE SIZE"}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60 mb-3">Quantity</div>
                <div data-testid="product-qty-control" className="inline-flex items-stretch border-2 border-black bg-white text-black">
                  <button
                    data-testid="product-qty-minus"
                    onClick={() => setQty(q => Math.max(1, q-1))}
                    aria-label="Decrease quantity"
                    className="px-4 py-3 text-black hover:bg-black hover:text-white transition-colors border-r-2 border-black"
                  >
                    <Minus className="w-5 h-5" strokeWidth={2.5}/>
                  </button>
                  <div data-testid="product-qty" className="min-w-[72px] flex items-center justify-center px-4 font-mono text-xl text-black select-none">{qty}</div>
                  <button
                    data-testid="product-qty-plus"
                    onClick={() => setQty(q => Math.min(20, q+1))}
                    aria-label="Increase quantity"
                    className="px-4 py-3 text-black hover:bg-black hover:text-white transition-colors border-l-2 border-black"
                  >
                    <Plus className="w-5 h-5" strokeWidth={2.5}/>
                  </button>
                </div>
              </div>

              {err && <div className="mt-4 text-red-600 text-sm" data-testid="product-error">{err}</div>}

              {products_available ? (
                <button
                  data-testid="product-add-to-cart"
                  onClick={onAdd}
                  className="mt-8 w-full md:w-auto inline-flex items-center justify-center bg-black text-white font-display uppercase text-sm tracking-[0.22em] px-10 py-4 hover:bg-[#F4C10A] hover:text-black transition-colors"
                >
                  Add to Cart
                </button>
              ) : (
                <div className="mt-8">
                  <button
                    data-testid="product-available-soon"
                    type="button"
                    disabled
                    aria-disabled="true"
                    className="w-full md:w-auto inline-flex items-center justify-center bg-neutral-200 text-black/50 font-display uppercase text-sm tracking-[0.22em] px-10 py-4 cursor-not-allowed border border-black/15"
                  >
                    Available Soon
                  </button>
                  <div
                    data-testid="product-drop-coming-soon"
                    className="mt-2 font-mono text-[10px] tracking-[0.3em] uppercase text-black/50"
                  >
                    Online Drop Coming Soon
                  </div>
                </div>
              )}

              <div className="mt-10 pt-6 border-t border-black/15 font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
                Final Sale · No Returns or Exchanges
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
